import { Notifications as MantineNotifications, notifications as mantineNotifications } from "@mantine/notifications";
import type * as React from "react";

/**
 * The owned notification surface. One function, replacing the two vendor entry points
 * (`notifications.show` and the older `showNotification`) that 100 files imported directly.
 *
 * The options type is the measured subset of the vendor contract — a census over all 295 call
 * sites (SC-2700) found exactly these keys in use and none of `loading`, `withCloseButton`,
 * `onClose`, or any `update`/`hide`/`clean` call. The unused surface deliberately does not
 * exist here; the Mantine 7 upgrade renegotiates only what call sites actually spend.
 */
export interface NotificationOptions {
  /** Passing the same id twice while the first toast is open de-duplicates (retry storms). */
  id?: string;
  title?: React.ReactNode;
  message?: React.ReactNode;
  /** Theme color key — "red" and "green" carry 252 of the 295 call sites. */
  color?: string;
  /** ms until auto-dismiss; `false` keeps the toast until closed. Vendor default when omitted. */
  autoClose?: boolean | number;
  icon?: React.ReactNode;
  onClick?: () => void;
}

export const showNotification = (options: NotificationOptions): void => {
  // Mantine's type requires the `message` key (ReactNode, null allowed) while the owned type keeps
  // it optional. Every call site today passes the key (sometimes with an undefined value, which
  // the spread deliberately lets override the fill — identical to what the vendor received
  // before); the null default exists so a future title-only call satisfies the vendor type.
  // Spread order is load-bearing and pinned by notifications.test.ts.
  mantineNotifications.show({ message: null, ...options });
};

/**
 * The portal host `ShelfCycleProvider` mounts; not exported from the barrel. The owned annotation
 * is what keeps the vendor's `NotificationsProps` type out of the emitted declarations
 * (`lint:ui-types` gate) — the host is always rendered bare.
 */
export const NotificationsHost: React.ComponentType = MantineNotifications;
